import { defineStore } from "pinia";
import {
  GlobalEditMode,
  EditMode,
  EditModeNotationType,
  NotationType,
} from "common/unions";
import { ref } from "vue";

/** Tools waiting for a click/drag. A printable key should drop the tool and type into the cell. */
export const ARMED_TOOL_EDIT_MODES: EditMode[] = [
  "EXPONENT_STARTED",
  "AREA_SELECTION_STARTED",
  "TEXT_STARTED",
  "ANNOTATION_STARTED",
  "POLYGON_STARTED",
  "LINE_STARTED",
  "DIVISIONLINE_STARTED",
  "CURVE_STARTED",
  "CIRCLE_STARTED",
  "PARABOLA_STARTED",
  "HYPERBOLA_STARTED",
  "SQRT_STARTED",
  "FREE_SKETCH_STARTED",
  "FREE_SKETCH_WITH_OCR_STARTED",
  "CHECKMARK_STARTED",
  "SEMICHECKMARK_STARTED",
  "XMARK_STARTED",
  "CARTESIAN_SYSTEM_STARTED",
  "COLORIZING",
  "DELETING",
];

export const useEditModeStore = defineStore("editMode", () => {
  let editMode = ref<EditMode>("CELL_SELECTED");
  let globalEditMode = ref<GlobalEditMode>("TEXT");

  const defaultEditMode: EditMode = "CELL_SELECTED";

  function setGlobalEditMode(mode: GlobalEditMode) {
    console.debug(
      `old global edit mode: ${globalEditMode.value}, new global edit mode: ${mode} `,
    );

    globalEditMode.value = mode;
  }

  function getGlobalEditMode() {
    return globalEditMode.value;
  }

  function isAreaSelectionOrMovingMode() {
    return (
      editMode.value === "AREA_SELECTION_STARTED" ||
      editMode.value === "AREA_SELECTED" ||
      editMode.value === "TEXT_AREA_SELECTING" ||
      editMode.value === "AREA_MOVING"
    );
  }

  function isFreeSketchDrawingMode() {
    return editMode.value === "FREE_SKETCH_DRAWING";
  }

  function isFreeSketchSelectedMode() {
    return editMode.value === "FREE_SKETCH_SELECTED";
  }

  function isFreeSketchStartedMode() {
    return editMode.value === "FREE_SKETCH_STARTED";
  }

  function isFreeSketchOcrDrawingMode() {
    return editMode.value === "FREE_SKETCH_WITH_OCR_DRAWING";
  }

  function isFreeSketchOcrStartedMode() {
    return editMode.value === "FREE_SKETCH_WITH_OCR_STARTED";
  }

  function isResizeMode() {
    return editMode.value === "RESIZE_STARTED" || editMode.value === "RESIZING";
  }

  function isAreaSelectedMode() {
    return editMode.value === "AREA_SELECTED";
  }

  function isCurveEditingControlPointMode() {
    return editMode.value === "CURVE_EDITING_CONTROLֹ_POINT";
  }

  function isSelectionMode() {
    return (
      editMode.value === "AREA_SELECTION_STARTED" ||
      editMode.value === "AREA_MOVING"
    );
  }

  function isSelectedMode() {
    return (
      editMode.value === "CIRCLE_SELECTED" ||
      editMode.value === "CONIC_SELECTED" ||
      editMode.value === "AREA_SELECTED" ||
      editMode.value === "LINE_SELECTED" ||
      editMode.value === "DIVISIONLINE_SELECTED" ||
      editMode.value === "CURVE_SELECTED" ||
      editMode.value === "TEXT_SELECTED" ||
      editMode.value === "IMAGE_SELECTED" ||
      editMode.value === "ANNOTATION_SELECTED" ||
      editMode.value === "SQRT_SELECTED" ||
      editMode.value === "FREE_SKETCH_SELECTED"
    );
  }

  function isSelectFromListMode() {
    return (
      editMode.value === "LESSONS_SELECTION" ||
      editMode.value === "QUESTIONS_SELECTION" ||
      editMode.value === "ANSWERS_SELECTION"
    );
  }

  function isLineMode() {
    return (
      editMode.value === "LINE_DRAWING" ||
      editMode.value === "LINE_SELECTED" ||
      editMode.value === "LINE_EDITING_LEFT" ||
      editMode.value === "LINE_EDITING_RIGHT"
    );
  }

  function isDivisionLineMode() {
    return (
      editMode.value === "DIVISIONLINE_DRAWING" ||
      editMode.value === "DIVISIONLINE_SELECTED" ||
      editMode.value === "DIVISIONLINE_EDITING_LEFT" ||
      editMode.value === "DIVISIONLINE_EDITING_RIGHT"
    );
  }

  function isPolygonDrawingMode() {
    return editMode.value === "POLYGON_DRAWING";
  }

  function isAreaSelectingMode() {
    return editMode.value === "AREA_SELECTION_STARTED";
  }

  function isSqrtMode() {
    return (
      editMode.value === "SQRT_STARTED" ||
      editMode.value === "SQRT_DRAWING" ||
      editMode.value === "SQRT_SELECTED"
    );
  }

  function isDefaultEditMode() {
    return editMode.value === defaultEditMode;
  }

  function isSqrtEditMode() {
    return editMode.value === "SQRT_EDITING";
  }

  function isSqrtSelectedMode() {
    return editMode.value === "SQRT_SELECTED";
  }

  function isSqrtDrawingMode() {
    return editMode.value === "SQRT_DRAWING";
  }

  function isLineStartedMode() {
    return editMode.value === "LINE_STARTED";
  }

  function isDivisionLineStartedMode() {
    return editMode.value === "DIVISIONLINE_STARTED";
  }

  function isCurveStartedMode() {
    return editMode.value === "CURVE_STARTED";
  }

  function isSqrtStartedMode() {
    return editMode.value === "SQRT_STARTED";
  }

  function isLineDrawingMode() {
    return editMode.value === "LINE_DRAWING";
  }

  function isDivisionLineDrawingMode() {
    return editMode.value === "DIVISIONLINE_DRAWING";
  }

  function isLineEditingMode() {
    return (
      editMode.value === "LINE_EDITING_LEFT" ||
      editMode.value === "LINE_EDITING_RIGHT"
    );
  }

  function isDivisionLineEditingMode(): boolean {
    return (
      editMode.value === "DIVISIONLINE_EDITING_LEFT" ||
      editMode.value === "DIVISIONLINE_EDITING_RIGHT"
    );
  }

  function isCurveDrawingMode() {
    return (
      editMode.value === "CURVE_DRAWING" ||
      editMode.value === "CURVE_EDITING_LEFT" ||
      editMode.value === "CURVE_EDITING_RIGHT" ||
      editMode.value === "CURVE_EDITING_CONTROLֹ_POINT"
    );
  }

  function isCircleDrawingMode() {
    return (
      editMode.value === "CIRCLE_DRAWING" || editMode.value === "CIRCLE_EDITING"
    );
  }

  function isLineSelectedMode() {
    return editMode.value === "LINE_SELECTED";
  }

  function isDivisionLineSelectedMode() {
    return editMode.value === "DIVISIONLINE_SELECTED";
  }

  function isCurveSelectedMode() {
    return editMode.value === "CURVE_SELECTED";
  }

  function isCircleSelectedMode() {
    return editMode.value === "CIRCLE_SELECTED";
  }

  function isConicDrawingMode() {
    return (
      editMode.value === "CONIC_DRAWING" ||
      editMode.value === "CONIC_EDITING_VERTEX" ||
      editMode.value === "CONIC_EDITING_SCALE" ||
      editMode.value === "CONIC_EDITING_OPENING" ||
      editMode.value === "CONIC_EDITING_THROUGH"
    );
  }

  function isConicSelectedMode() {
    return editMode.value === "CONIC_SELECTED";
  }

  function isTextStartedMode() {
    return editMode.value === "TEXT_STARTED";
  }

  function isTextSelectedMode() {
    return editMode.value === "TEXT_SELECTED";
  }

  function isImageSelectedMode() {
    return editMode.value === "IMAGE_SELECTED";
  }

  function isImageCroppingMode() {
    return editMode.value === "IMAGE_CROPPING";
  }

  function isTextSelectionMode() {
    return editMode.value === "TEXT_AREA_SELECTING";
  }

  function isTextWritingMode() {
    return editMode.value === "TEXT_WRITING";
  }

  function isAnnotationStartedMode() {
    return editMode.value === "ANNOTATION_STARTED";
  }

  function isAnnotationSelectedMode() {
    return editMode.value === "ANNOTATION_SELECTED";
  }

  function isAnnotationWritingMode() {
    return editMode.value === "ANNOTATION_WRITING";
  }

  function isExponentStartedMode() {
    return editMode.value === "EXPONENT_STARTED";
  }

  function isExponentWritingMode() {
    return editMode.value === "EXPONENT_WRITING";
  }

  function isExponentMode() {
    return (
      editMode.value === "EXPONENT_STARTED" ||
      editMode.value === "EXPONENT_WRITING" ||
      editMode.value === "EXPONENT_SELECTED"
    );
  }

  function isCheckMode() {
    return (
      editMode.value === "CHECKMARK_STARTED" ||
      editMode.value === "SEMICHECKMARK_STARTED" ||
      editMode.value === "XMARK_STARTED"
    );
  }

  function isColorizingMode() {
    return editMode.value === "COLORIZING";
  }

  function isArmedToolMode() {
    return ARMED_TOOL_EDIT_MODES.includes(editMode.value);
  }

  function getEditMode(): EditMode {
    return editMode.value;
  }

  function getDefaultEditMode() {
    return defaultEditMode;
  }

  function setEditMode(newEditMode: EditMode) {
    console.debug(
      `old edit mode: ${editMode.value}, new edit mode: ${newEditMode} `,
    );
    console.trace();
    editMode.value = newEditMode;
  }

  function setDefaultEditMode() {
    setGlobalEditMode("TEXT");
    setEditMode(defaultEditMode);
  }

  function getNotationTypeByEditMode(): NotationType {
    return EditModeNotationType.get(getEditMode())!;
  }

  function isLineGlobalMode() {
    return globalEditMode.value === "LINE";
  }

  function isAnnotationGlobalMode() {
    return globalEditMode.value === "ANNOTATION";
  }

  function isFreeSketchGlobalMode() {
    return globalEditMode.value === "FREE_SKETCH";
  }

  function isFreeSketchOcrGlobalMode() {
    return globalEditMode.value === "FREE_SKETCH_WITH_OCR";
  }

  function isInGlobalDrawingMode() {
    return globalEditMode.value !== "TEXT";
  }

  function isDrawingOrSelectingMode() {
    return (
      isLineStartedMode ||
      isLineEditingMode() ||
      isLineDrawingMode() ||
      isCurveStartedMode() ||
      isCurveDrawingMode() ||
      isSqrtStartedMode() ||
      isSqrtDrawingMode() ||
      isDivisionLineStartedMode() ||
      isDivisionLineDrawingMode() ||
      isDivisionLineEditingMode() ||
      isFreeSketchStartedMode() ||
      isFreeSketchOcrStartedMode() ||
      isPolygonDrawingMode() ||
      isCurveEditingControlPointMode() ||
      isCircleDrawingMode() ||
      isCircleSelectedMode() ||
      isConicDrawingMode() ||
      isConicSelectedMode() ||
      isTextStartedMode() ||
      isAreaSelectingMode()
    );
  }

  return {
    getEditMode,
    getGlobalEditMode,
    getDefaultEditMode,
    getNotationTypeByEditMode,
    isAreaSelectedMode,
    isAreaSelectingMode,
    isAreaSelectionOrMovingMode,
    isExponentMode,
    isExponentStartedMode,
    isExponentWritingMode,
    isSelectionMode,
    isLineMode,
    isDivisionLineMode,
    isSqrtStartedMode,
    isSqrtDrawingMode,
    isLineSelectedMode,
    isDivisionLineSelectedMode,
    isCurveSelectedMode,
    isLineStartedMode,
    isDivisionLineStartedMode,
    isCurveStartedMode,
    isLineDrawingMode,
    isDivisionLineDrawingMode,
    isLineEditingMode,
    isDivisionLineEditingMode,
    isCurveDrawingMode,
    isCircleDrawingMode,
    isCircleSelectedMode,
    isConicDrawingMode,
    isConicSelectedMode,
    isSelectedMode,
    isSqrtMode,
    isSqrtEditMode,
    isSqrtSelectedMode,
    isColorizingMode,
    isArmedToolMode,
    isDefaultEditMode,
    isTextWritingMode,
    isTextStartedMode,
    isTextSelectedMode,
    isTextSelectionMode,
    isImageSelectedMode,
    isImageCroppingMode,
    isAnnotationWritingMode,
    isAnnotationStartedMode,
    isAnnotationSelectedMode,
    isCheckMode,
    isResizeMode,
    isSelectFromListMode,
    isPolygonDrawingMode,
    isCurveEditingControlPointMode,
    isFreeSketchDrawingMode,
    isFreeSketchSelectedMode,
    isFreeSketchStartedMode,
    isFreeSketchOcrDrawingMode,
    isFreeSketchOcrStartedMode,
    isLineGlobalMode,
    isAnnotationGlobalMode,
    isFreeSketchGlobalMode,
    isFreeSketchOcrGlobalMode,
    isInGlobalDrawingMode,
    isDrawingOrSelectingMode,
    setEditMode,
    setGlobalEditMode,
    setDefaultEditMode,
  };
});
